import clsx from 'clsx';
import { Icon } from 'common/icon';
import Image from 'next/image';
import { useCallback, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';

import styles from './styles.module.css';

import { InputField } from 'common';
import { Rating } from './rating';
import { Button } from 'common/new-button';
import { ICasino } from 'constants/types';
import { Formik, FormikValues } from 'formik';
import { useAlert } from 'components';
import { useCreateCasinoReviewsMutation } from 'hooks/api/casino_reviews';
import { useMeQuery } from 'hooks/api/users';
import { handleMutation } from 'utils/handleMutation';
import { initialValues, model, validationSchema } from './config';

import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';

import { ICasinoReview } from 'constants/types';
import { useClickOutside } from 'hooks/useClickOutside';
import { Routes } from 'constants/routes';

type Props = {
  casino: ICasino;
  isVisible: boolean;
  setVisible: (isVisible: boolean) => void;
  onSuccess?: () => void;
};

export const CasinoReviewModal = ({
  onSuccess,
  isVisible,
  setVisible,
  casino,
}: Props) => {
  const alert = useAlert();
  const router = useRouter();

  const modalRef = useRef<HTMLDivElement | null>(null);

  const { data: me } = useMeQuery();

  const [placeReview, mutation] = useCreateCasinoReviewsMutation();
  const isLoading = mutation.isLoading;

  const initialReviewValues = useMemo(() => {
    if (me?.user?.reviews && me.user.reviews.length > 0) {
      const current = me?.user?.reviews.find(
        (review: ICasinoReview) => review.casino_id === casino._id
      );
      if (current) {
        return current;
      }
    }
    return {};
  }, [me?.user?.reviews, casino._id]);

  const onReviewAdded = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    }
  }, [onSuccess]);

  handleMutation({ ...mutation, onSuccess: onReviewAdded });

  const onSubmit = async (values: FormikValues) => {
    const { review, rating } = values;
    if (!rating) {
      alert.error('Please select rating');
      return;
    }

    if (!me?.user?._id) {
      router.push(Routes.login);
      return;
    }

    await placeReview({
      casinoId: casino._id,
      review,
      rating,
    });

    alert.success('Review successfully added');
    setVisible(false);
  };

  const handleCloseModal = () => {
    setVisible(false);
  };

  useClickOutside({ onClick: handleCloseModal, ref: modalRef });

  if (!isVisible) {
    return null;
  }

  return (
    <div className={clsx(styles.modalBody, styles.modalBodyActive)}>
      <div className={styles.container}>
        <Formik
          onSubmit={onSubmit}
          initialValues={initialValues(initialReviewValues)}
          validationSchema={validationSchema}
        >
          {({ dirty, isSubmitting, setFieldValue, submitForm, values }) => (
            <div className={styles.wrapper} ref={modalRef}>
              <div className={styles.top}>
                <div className={styles.topLeft}>
                  <div className={styles.image}>
                    <LogoOverlayWrapper>
                      <LogoOverlay
                        width={60}
                        height={60}
                        alt={casino?.name}
                        image={casino?.logoUrl}
                      />

                      <Image
                        src={casino?.imageUrl}
                        alt="review-image"
                        width={60}
                        height={60}
                      />
                    </LogoOverlayWrapper>
                  </div>

                  <div className={styles.text}>
                    <span>{casino?.name}</span>
                  </div>
                </div>

                <div
                  className={styles.closeButton}
                  onClick={() => setVisible(false)}
                >
                  <Icon.Close />
                </div>
              </div>

              <div className={styles.rating}>
                <Rating
                  value={values.rating || 0}
                  onChange={(rating) => setFieldValue('rating', rating)}
                  variant={'casino'}
                />
              </div>
              <div className={styles.multiTextInput}>
                <InputField {...model.review} />
              </div>
              <div className={styles.submitButton}>
                <Button
                  text={isLoading ? 'Loading...' : 'Send review'}
                  onClick={submitForm}
                  disabled={!dirty || isSubmitting}
                />
              </div>
            </div>
          )}
        </Formik>
      </div>
    </div>
  );
};
